"use client"

import React, { useMemo } from "react"
import { ChevronDownIcon, PlusIcon, XCircleIcon } from "lucide-react"
import { useTranslations } from "next-intl"
import { useDebounceValue } from "usehooks-ts"

import { ExtendedAddressRO } from "@/lib/storefront/addresses/address"
import { Button, Input } from "@/components/ui/components"
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/components/sheet"
import { AddressFields } from "@/components/common/addresses/AddressFields"

interface AddressSelectSheetProps {
  addresses: ExtendedAddressRO[]
  selectedAddress?: ExtendedAddressRO
  onSelect?: (a: ExtendedAddressRO) => void
  isB2B?: boolean
}
const defaultValue = ""

/**
 * Filtert eine Liste von Adressen basierend auf einem Suchbegriff.
 * Die Suche ist case-insensitiv und durchsucht eine vordefinierte
 * Liste von Adressfeldern.
 *
 * @param addresses - Das Array von AddressRO-Objekten, das gefiltert werden soll.
 * @param searchTerm - Der Suchbegriff, nach dem gesucht wird.
 * @returns Ein neues Array mit den passenden AddressRO-Objekten.
 */
function filterAddresses(addresses: ExtendedAddressRO[], searchTerm: string): ExtendedAddressRO[] {
  // 1. Bereinige den Suchbegriff: Entferne Leerzeichen und konvertiere
  //    zu Kleinbuchstaben für einen case-insensitiven Vergleich.
  const normalizedSearchTerm = searchTerm.trim().toLowerCase()

  // Wenn der Suchbegriff leer ist, gib alle Adressen zurück.
  if (!normalizedSearchTerm) {
    return addresses
  }

  // 2. Definiere die Felder, die durchsucht werden sollen.
  //    Dies macht den Code lesbar und einfach erweiterbar.
  const fieldsToSearch: (keyof ExtendedAddressRO)[] = [
    "firstName",
    "lastName",
    "companyName1",
    "companyName2",
    "addressLine1",
    "addressLine2",
    "addressLine3",
    "postalCode",
    "city",
  ]

  // 3. Filtere das Adress-Array.
  return addresses.filter((address) => {
    // 4. Prüfe für jede Adresse, ob EINES der zu durchsuchenden Felder
    //    den Suchbegriff enthält. `some` stoppt, sobald ein Treffer gefunden wird.
    return fieldsToSearch.some((field) => {
      const value = address[field]

      // Stelle sicher, dass der Wert ein String ist, bevor toLowerCase()
      // und includes() aufgerufen werden, um Fehler zu vermeiden.
      return typeof value === "string" && value.toLowerCase().includes(normalizedSearchTerm)
    })
  })
}

function isSameAddress(address: ExtendedAddressRO, selectedAddress?: ExtendedAddressRO): boolean {
  if (!selectedAddress) {
    return false
  }

  return address.urn === selectedAddress.urn || address.id === selectedAddress.id
}

export default function AddressSelectSheet({ addresses, selectedAddress, onSelect, isB2B }: AddressSelectSheetProps) {
  const t = useTranslations("storefront")
  const [open, setOpen] = React.useState(false)
  const [debouncedValue, setValue] = useDebounceValue(defaultValue, 10)

  const selectableAddresses = useMemo(() => {
    return addresses.filter((address) => !isSameAddress(address, selectedAddress))
  }, [addresses, selectedAddress])

  function onSaveInternal(urn?: string) {
    if (onSelect) {
      const selectedAddress = selectableAddresses.find((a) => {
        return a?.urn === urn
      })

      if (selectedAddress) {
        onSelect(selectedAddress)
      }
    }
    setOpen(false)
    //window.location.reload();
  }

  const filteredAddresses = useMemo(() => {
    if (debouncedValue && debouncedValue.length > 0) {
      return filterAddresses(selectableAddresses, debouncedValue)
    } else {
      return selectableAddresses
    }
  }, [debouncedValue, selectableAddresses])

  function clearSearchTerm() {
    console.log("clear search term")
    setValue("")
  }

  if (selectableAddresses.length === 0) {
    return null
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button size={"smNoGrow"} variant={"outline_secondary"} className="">
          <ChevronDownIcon strokeWidth={1} className="inline" />
          <span className="ml-2">{t("forms.edit_address_form.select")}</span>
        </Button>
      </SheetTrigger>
      <SheetContent side={"right"} className={"bg-ub-background"}>
        <SheetTitle className={"text-lg font-bold"}> {t("forms.edit_address_form.select")}</SheetTitle>
        <div className={"flex flex-col gap-2"}>
          <div className="relative p-1">
            <Input
              className={"peer w-full pe-9"}
              type="text"
              value={debouncedValue}
              onChange={(event) => setValue(event.target.value)}
            />
            <div className="absolute inset-y-0 end-0 flex items-center justify-center pe-3 text-muted-foreground/80">
              <Button size={"tiny"} variant={"ghost"} onClick={clearSearchTerm}>
                <XCircleIcon className={"size-5"} aria-hidden="true" />
              </Button>
            </div>
          </div>

          <div className="size-full max-h-screen overflow-y-auto pb-40">
            <div className={"flex w-full flex-col p-1"}>
              <div className={"flex w-full flex-col gap-2"}>
                {filteredAddresses?.map((a) => (
                  <button
                    key={a.urn}
                    className={`
                      min-h-24 rounded-sm border border-gray-200 bg-white p-2
                      hover:outline-offset-1 hover:outline-primary hover:outline-solid
                      sm:p-4
                    `}
                    onClick={() => onSaveInternal(a?.urn)}
                  >
                    <AddressFields className={"text-sm"} address={a} isB2B={isB2B ?? false} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
